// Empty-state prompts for the portfolio editor preview (EPD-10137).
//
// Rendered ONLY in the editor preview — the public page omits empty blocks
// entirely (EPD-10124). Both variants inherit the active theme via `--pf-*`
// tokens. EmptyPrompt is the standard block-level treatment; EmptyPill is the
// inline variant for tight spaces (e.g. the hero social-handle row).

import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";
import { Plus } from "lucide-react";

const HIDDEN_NOTE =
  "Incomplete sections are hidden on your published page until you add this info.";

interface EmptyCta {
  label: string;
  href: string;
}

interface EmptyPromptProps {
  message: string;
  title?: string;
  icon?: LucideIcon;
  cta?: EmptyCta;
  /** The "Incomplete sections are hidden…" reassurance line. Default shown. */
  showHiddenNote?: boolean;
  className?: string;
}

export function EmptyPrompt({
  message,
  title,
  icon: Icon = Plus,
  cta,
  showHiddenNote = true,
  className,
}: EmptyPromptProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center gap-2 rounded-[inherit] border border-dashed px-6 py-7 text-center",
        className,
      )}
      style={{ borderColor: "var(--pf-border)" }}
    >
      <span
        className="flex h-8 w-8 items-center justify-center rounded-full"
        style={{ background: "var(--pf-surface)", color: "var(--pf-accent)" }}
      >
        <Icon className="h-4 w-4" />
      </span>
      {title && (
        <span
          className="text-sm font-semibold"
          style={{
            fontFamily: "var(--pf-font-heading)",
            color: "var(--pf-heading)",
          }}
        >
          {title}
        </span>
      )}
      <p
        className="max-w-[360px] text-[12.5px]"
        style={{ color: "var(--pf-muted)" }}
      >
        {message}
      </p>
      {cta && (
        <a
          href={cta.href}
          className="text-[12.5px] font-medium underline underline-offset-2 transition-opacity hover:opacity-80"
          style={{ color: "var(--pf-accent)" }}
        >
          {cta.label}
        </a>
      )}
      {showHiddenNote && (
        <p
          className="max-w-[320px] text-[11px] opacity-75"
          style={{ color: "var(--pf-muted)" }}
        >
          {HIDDEN_NOTE}
        </p>
      )}
    </div>
  );
}

interface EmptyPillProps {
  message: string;
  icon?: LucideIcon;
  cta?: EmptyCta;
  className?: string;
}

export function EmptyPill({
  message,
  icon: Icon = Plus,
  cta,
  className,
}: EmptyPillProps) {
  return (
    <span
      className={cn(
        // flex-wrap + max-w-full: on a phone the CTA wraps under the message
        // instead of pushing the pill past the hero's edge.
        "inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-[18px] border border-dashed px-3 py-1.5 text-[12px]",
        className,
      )}
      style={{ borderColor: "var(--pf-border)", color: "var(--pf-muted)" }}
    >
      <Icon className="h-3.5 w-3.5" style={{ color: "var(--pf-accent)" }} />
      <span>{message}</span>
      {cta && (
        <a
          href={cta.href}
          className="font-medium underline underline-offset-2 transition-opacity hover:opacity-80"
          style={{ color: "var(--pf-accent)" }}
        >
          {cta.label}
        </a>
      )}
    </span>
  );
}
